import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import {
  Plus,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Users,
  X,
  Info,
  Rocket,
  ArrowRight,
} from "lucide-react";
import {
  deploymentRequestsApi,
  servicesApi,
  environmentsApi,
  approvalRolesApi,
  DeploymentRequest,
  DeploymentRequestField,
  EnvironmentApprover,
  Service,
  EligibleApprover,
} from "@/api/client";
import EnvironmentSelect from "@/components/EnvironmentSelect";
import ServiceSelect from "@/components/ServiceSelect";
import DeploymentDetailsModal from "@/components/DeploymentDetailsModal";
import { formatDate, titleCase } from "@/lib/utils";
import DynamicFormField from "@/components/DynamicFormField";

interface ApproverInput {
  role_id?: string;
  role: string;
  email: string;
  name?: string;
  is_required?: boolean;
  is_preconfigured?: boolean;
}

export default function DeploymentRequests() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showForm, setShowForm] = useState(false);
  const [selectedRequest, setSelectedRequest] =
    useState<DeploymentRequest | null>(null);
  const [selectedDeploymentId, setSelectedDeploymentId] = useState<
    string | null
  >(null);
  const [filters, setFilters] = useState({
    status: "",
    service: "",
    environment: "",
  });

  // Open form if ?new=true is in URL (e.g., from Dashboard button)
  useEffect(() => {
    if (searchParams.get("new") === "true") {
      setShowForm(true);
      // Remove the query param so refreshing doesn't reopen the form
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Open modal if ?id=... is in URL (e.g., from DeploymentDetailsModal link)
  const requestIdFromUrl = searchParams.get("id");
  const { data: requestFromUrl } = useQuery({
    queryKey: ["deployment-request", requestIdFromUrl],
    queryFn: () => deploymentRequestsApi.get(requestIdFromUrl!),
    enabled: !!requestIdFromUrl && !selectedRequest,
  });

  useEffect(() => {
    if (requestFromUrl?.deployment_request && !selectedRequest) {
      setSelectedRequest(requestFromUrl.deployment_request);
      // Clear the URL param after opening the modal
      setSearchParams({}, { replace: true });
    }
  }, [requestFromUrl, selectedRequest, setSearchParams]);

  // Form state
  const [selectedService, setSelectedService] = useState("");
  const [selectedServiceGroupId, setSelectedServiceGroupId] = useState<
    string | null
  >(null);
  const [selectedEnvironment, setSelectedEnvironment] = useState("");
  const [customFieldValues, setCustomFieldValues] = useState<
    Record<string, string>
  >({});
  const [approvers, setApprovers] = useState<ApproverInput[]>([]);
  const [newApprover, setNewApprover] = useState({
    role_id: "",
    role: "",
    email: "",
    name: "",
  });
  const [eligibleApprovers, setEligibleApprovers] = useState<
    EligibleApprover[]
  >([]);
  const [loadingEligible, setLoadingEligible] = useState(false);

  const queryClient = useQueryClient();

  const { data: deploymentRequests, isLoading } = useQuery({
    queryKey: ["deployment-requests", filters],
    queryFn: () =>
      deploymentRequestsApi.list({
        status: filters.status || undefined,
        service: filters.service || undefined,
        environment: filters.environment || undefined,
        limit: 50,
      }),
  });

  const { data: services } = useQuery({
    queryKey: ["services"],
    queryFn: () => servicesApi.list(),
  });

  const { data: environments } = useQuery({
    queryKey: ["environments"],
    queryFn: () => environmentsApi.list(),
  });

  // Fetch approval roles for the approver dropdown
  const { data: approvalRolesData } = useQuery({
    queryKey: ["approval-roles", { active_only: true }],
    queryFn: () => approvalRolesApi.list(true),
  });

  const approvalRoles = approvalRolesData?.roles || [];

  // Fetch form data when environment changes
  const { data: formData, isLoading: formDataLoading } = useQuery({
    queryKey: ["deployment-request-form-data", selectedEnvironment],
    queryFn: () =>
      deploymentRequestsApi.getFormData(selectedEnvironment || undefined),
    enabled: showForm,
  });

  // Update approvers when form data loads
  useEffect(() => {
    if (formData?.approvers && selectedEnvironment) {
      const preconfiguredApprovers: ApproverInput[] = formData.approvers.map(
        (a: EnvironmentApprover) => ({
          role_id: a.role_id,
          role: a.role_slug || a.role_name || "approver",
          email: a.user_email,
          name: a.user_name,
          is_required: a.is_required,
          is_preconfigured: true,
        }),
      );
      setApprovers(preconfiguredApprovers);
    } else if (!selectedEnvironment) {
      setApprovers([]);
    }
  }, [formData?.approvers, selectedEnvironment]);

  // Initialize custom field values with defaults
  useEffect(() => {
    if (formData?.custom_fields) {
      const defaults: Record<string, string> = {};
      formData.custom_fields.forEach((field: DeploymentRequestField) => {
        defaults[field.slug] = field.default_value || "";
      });
      setCustomFieldValues(defaults);
    }
  }, [formData?.custom_fields]);

  const createMutation = useMutation({
    mutationFn: deploymentRequestsApi.create,
    onSuccess: (data) => {
      // Invalidate all related queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ["deployment-requests"] });
      queryClient.invalidateQueries({ queryKey: ["my-approvals"] });
      queryClient.invalidateQueries({ queryKey: ["deployments"] });
      queryClient.invalidateQueries({ queryKey: ["release-cycles"] });
      resetForm();
      // In dev mode, show the approval links
      if (data.approval_links && data.approval_links.length > 0) {
        alert(
          "Approval Links (dev mode):\n\n" +
            data.approval_links
              .map((l) => `${l.role} (${l.email}): ${l.url}`)
              .join("\n"),
        );
      }
    },
  });

  const resetForm = () => {
    setShowForm(false);
    setSelectedService("");
    setSelectedServiceGroupId(null);
    setSelectedEnvironment("");
    setCustomFieldValues({});
    setApprovers([]);
    setNewApprover({ role_id: "", role: "", email: "", name: "" });
  };

  // Handle service selection - also updates the group filter for environments
  const handleServiceChange = (value: string, service?: Service) => {
    setSelectedService(value);
    setSelectedServiceGroupId(service?.group_id || null);
    // Reset environment when service changes since it may no longer be valid
    setSelectedEnvironment("");
  };

  // Filter environments by the selected service's group
  const filteredEnvironments = (environments?.environments ?? []).filter(
    (env) => {
      if (!selectedServiceGroupId) {
        // If no service selected or service has no group, show all environments
        return true;
      }
      // Show environments that belong to the same group as the selected service
      return env.group_id === selectedServiceGroupId;
    },
  );

  // Get the selected environment's details to check if approval is required
  const selectedEnvironmentDetails = filteredEnvironments.find(
    (env) => env.slug === selectedEnvironment,
  );
  const requiresApproval =
    selectedEnvironmentDetails?.approval_required ?? true;

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // Build approvers array
    const approversList = approvers.map((a) => ({
      role_id: a.role_id,
      role: a.role,
      email: a.email,
      name: a.name,
    }));

    createMutation.mutate({
      service: formData.get("service") as string,
      environment: formData.get("environment") as string,
      version: formData.get("version") as string,
      approvers: approversList,
      custom_fields: customFieldValues,
      notes: formData.get("notes") as string,
      expires_in: (formData.get("expires_in") as string) || "24h",
    });
  };

  // Fetch eligible approvers when role changes
  const handleRoleChange = async (roleId: string) => {
    setNewApprover({ ...newApprover, role_id: roleId, email: "", name: "" });
    setEligibleApprovers([]);

    if (roleId) {
      setLoadingEligible(true);
      try {
        const result = await approvalRolesApi.getEligibleApprovers(roleId);
        setEligibleApprovers(result.approvers || []);
      } catch (error) {
        console.error("Failed to fetch eligible approvers:", error);
      } finally {
        setLoadingEligible(false);
      }
    }
  };

  // Handle selecting an eligible approver from the dropdown
  const handleSelectEligibleApprover = (approver: EligibleApprover) => {
    setNewApprover({
      ...newApprover,
      email: approver.email,
      name: approver.name,
    });
  };

  const handleAddApprover = () => {
    if (newApprover.role_id && newApprover.email) {
      // Find the role to get its slug/name
      const selectedRole = approvalRoles.find(
        (r) => r.id === newApprover.role_id,
      );
      if (!selectedRole) return;

      setApprovers([
        ...approvers,
        {
          role_id: newApprover.role_id,
          role: selectedRole.slug,
          email: newApprover.email,
          name: newApprover.name || undefined,
          is_preconfigured: false,
        },
      ]);
      setNewApprover({ role_id: "", role: "", email: "", name: "" });
      setEligibleApprovers([]);
    }
  };

  const handleRemoveApprover = (index: number) => {
    const approver = approvers[index];
    // Only allow removing non-required pre-configured approvers or manually added ones
    if (approver.is_preconfigured && approver.is_required) {
      return; // Can't remove required pre-configured approvers
    }
    setApprovers(approvers.filter((_, i) => i !== index));
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return (
          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800">
            <Clock className="h-3 w-3 mr-1" />
            Pending
          </span>
        );
      case "approved":
        return (
          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="h-3 w-3 mr-1" />
            Approved
          </span>
        );
      case "rejected":
        return (
          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800">
            <XCircle className="h-3 w-3 mr-1" />
            Rejected
          </span>
        );
      case "expired":
        return (
          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-800">
            <AlertCircle className="h-3 w-3 mr-1" />
            Expired
          </span>
        );
      case "completed":
        return (
          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800">
            <CheckCircle className="h-3 w-3 mr-1" />
            Completed
          </span>
        );
      default:
        return null;
    }
  };

  const activeCustomFields = (formData?.custom_fields || []).filter(
    (f: DeploymentRequestField) => f.is_active,
  );

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Deployment Requests
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage deployment requests and approvals
          </p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="btn btn-primary"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Request
        </button>
      </div>

      {/* Create Form */}
      {showForm && (
        <div className="mt-6 card">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            Create Deployment Request
          </h2>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label htmlFor="service" className="label">
                  Service
                </label>
                <ServiceSelect
                  value={selectedService}
                  onChange={handleServiceChange}
                  className="input w-full"
                  required
                />
                <input type="hidden" name="service" value={selectedService} />
              </div>
              <div>
                <label htmlFor="environment" className="label">
                  Environment
                </label>
                <select
                  name="environment"
                  id="environment"
                  className="input"
                  required
                  value={selectedEnvironment}
                  onChange={(e) => setSelectedEnvironment(e.target.value)}
                  disabled={!selectedService}
                >
                  <option value="">
                    {!selectedService
                      ? "Select a service first"
                      : filteredEnvironments.length === 0
                        ? "No environments for this group"
                        : "Select an environment"}
                  </option>
                  {filteredEnvironments.map((env) => (
                    <option key={env.id} value={env.slug}>
                      {env.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="version" className="label">
                  Version
                </label>
                <input
                  type="text"
                  name="version"
                  id="version"
                  className="input"
                  placeholder="1.2.3"
                  required
                />
              </div>
            </div>

            {/* Custom Fields */}
            {activeCustomFields.length > 0 && (
              <div className="border-t pt-4">
                <h3 className="text-sm font-medium text-gray-900 mb-3">
                  Request Details
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {activeCustomFields.map((field: DeploymentRequestField) => (
                    <DynamicFormField
                      key={field.id}
                      field={field}
                      value={customFieldValues[field.slug] || ""}
                      onChange={(value) =>
                        setCustomFieldValues({
                          ...customFieldValues,
                          [field.slug]: value,
                        })
                      }
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Approvers - only show if environment requires approval */}
            {requiresApproval ? (
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Approvers
                  </h3>
                  {formDataLoading && selectedEnvironment && (
                    <span className="text-xs text-gray-500">
                      Loading configured approvers...
                    </span>
                  )}
                </div>

                {approvers.length > 0 ? (
                  <div className="space-y-2 mb-4">
                    {approvers.map((approver, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {approver.name || approver.email}
                            </div>
                            <div className="text-xs text-gray-500">
                              {approver.role} • {approver.email}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {approver.is_preconfigured && (
                            <span className="px-2 py-0.5 text-xs font-medium rounded bg-blue-100 text-blue-700">
                              Pre-configured
                            </span>
                          )}
                          {approver.is_required && (
                            <span className="px-2 py-0.5 text-xs font-medium rounded bg-red-100 text-red-700">
                              Required
                            </span>
                          )}
                          {(!approver.is_preconfigured ||
                            !approver.is_required) && (
                            <button
                              type="button"
                              onClick={() => handleRemoveApprover(index)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-lg p-4 mb-4 flex items-start gap-3">
                    <Info className="h-5 w-5 text-gray-400 mt-0.5" />
                    <p className="text-sm text-gray-500">
                      {selectedEnvironment
                        ? "No pre-configured approvers for this environment. Add approvers below."
                        : "Select an environment to load pre-configured approvers, or add them manually below."}
                    </p>
                  </div>
                )}

                {/* Add approver form */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-500 mb-3">
                    Add additional approvers:
                  </p>
                  {approvalRoles.length === 0 ? (
                    <p className="text-sm text-gray-500">
                      No approval roles defined. Create approval roles in
                      Settings → Approval Roles first.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      <div className="grid grid-cols-4 gap-3">
                        <div>
                          <select
                            value={newApprover.role_id}
                            onChange={(e) => handleRoleChange(e.target.value)}
                            className="input text-sm"
                          >
                            <option value="">Select Role</option>
                            {approvalRoles.map((role) => (
                              <option key={role.id} value={role.id}>
                                {role.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <input
                            type="email"
                            value={newApprover.email}
                            onChange={(e) =>
                              setNewApprover({
                                ...newApprover,
                                email: e.target.value,
                              })
                            }
                            className="input text-sm"
                            placeholder="Email"
                          />
                        </div>
                        <div>
                          <input
                            type="text"
                            value={newApprover.name}
                            onChange={(e) =>
                              setNewApprover({
                                ...newApprover,
                                name: e.target.value,
                              })
                            }
                            className="input text-sm"
                            placeholder="Name (optional)"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={handleAddApprover}
                          className="btn btn-secondary text-sm"
                          disabled={!newApprover.role_id || !newApprover.email}
                        >
                          Add
                        </button>
                      </div>

                      {/* Eligible approvers suggestions */}
                      {newApprover.role_id && (
                        <div className="ml-0">
                          {loadingEligible ? (
                            <p className="text-xs text-gray-500">
                              Loading eligible approvers...
                            </p>
                          ) : eligibleApprovers.length > 0 ? (
                            <div>
                              <p className="text-xs text-gray-500 mb-2">
                                Suggested approvers for this role:
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {eligibleApprovers.map((approver) => (
                                  <button
                                    key={approver.id}
                                    type="button"
                                    onClick={() =>
                                      handleSelectEligibleApprover(approver)
                                    }
                                    className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                                      newApprover.email === approver.email
                                        ? "bg-blue-600 text-white"
                                        : "bg-blue-100 text-blue-800 hover:bg-blue-200"
                                    }`}
                                  >
                                    <Users className="h-3 w-3 mr-1.5" />
                                    {approver.name || approver.email}
                                    {approver.role_name && (
                                      <span className="ml-1 opacity-75">
                                        ({approver.role_name})
                                      </span>
                                    )}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <p className="text-xs text-gray-400">
                              No pre-configured approvers for this role. Enter
                              email manually.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : selectedEnvironment ? (
              <div className="border-t pt-4">
                <div className="bg-blue-50 rounded-lg p-4 flex items-start gap-3">
                  <Info className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-800">
                      No Approval Required
                    </p>
                    <p className="text-xs text-blue-700 mt-1">
                      This environment does not require approval. The deployment
                      request will be recorded for tracking purposes.
                    </p>
                  </div>
                </div>
              </div>
            ) : null}

            {/* Deployer notification info */}
            {formData?.deployers && formData.deployers.length > 0 && (
              <div className="bg-green-50 rounded-lg p-4 flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-green-800">
                    Deployment Notifications Configured
                  </p>
                  <p className="text-xs text-green-700 mt-1">
                    When all approvals are complete,{" "}
                    {formData.deployers
                      .filter((d) => d.notify_on_approval)
                      .map((d) => d.user_name || d.user_email)
                      .join(", ")}{" "}
                    will be notified.
                  </p>
                </div>
              </div>
            )}

            {/* Options - only show expires/notes if approval is required */}
            {requiresApproval && (
              <div className="grid grid-cols-2 gap-4 border-t pt-4">
                <div>
                  <label htmlFor="expires_in" className="label">
                    Expires In
                  </label>
                  <select
                    name="expires_in"
                    id="expires_in"
                    className="input"
                    defaultValue="120h"
                  >
                    <option value="4h">4 hours</option>
                    <option value="24h">24 hours</option>
                    <option value="48h">48 hours</option>
                    <option value="72h">72 hours</option>
                    <option value="120h">5 days</option>
                    <option value="168h">7 days</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="notes" className="label">
                    Notes (optional)
                  </label>
                  <input
                    type="text"
                    name="notes"
                    id="notes"
                    className="input"
                    placeholder="Any notes for approvers..."
                  />
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 border-t pt-4">
              <button
                type="button"
                onClick={resetForm}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={
                  createMutation.isPending ||
                  (requiresApproval && approvers.length === 0)
                }
              >
                {createMutation.isPending ? "Creating..." : "Create Request"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filters */}
      <div className="mt-6 flex gap-4">
        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          className="input w-40"
        >
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="expired">Expired</option>
        </select>

        <select
          value={filters.service}
          onChange={(e) => setFilters({ ...filters, service: e.target.value })}
          className="input w-48"
        >
          <option value="">All Services</option>
          {(services?.services ?? []).map((svc) => (
            <option key={svc.id} value={svc.slug}>
              {svc.name}
            </option>
          ))}
        </select>

        <EnvironmentSelect
          value={filters.environment}
          onChange={(value) => setFilters({ ...filters, environment: value })}
          className="input w-48"
          valueKey="slug"
        />
      </div>

      {/* Deployment Requests Table */}
      <div className="mt-4 card overflow-hidden p-0">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Service
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Environment
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Version
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Approvers
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Expires
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(deploymentRequests?.data ?? []).map((request) => (
                <tr
                  key={request.id}
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => setSelectedRequest(request)}
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {titleCase(request.service_name)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {request.environment_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {request.version}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {getStatusBadge(request.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {request.required_approvals &&
                    request.required_approvals.length > 0
                      ? request.required_approvals
                          .map((a) => `${a.role} (${a.status})`)
                          .join(", ")
                      : "N/A"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {request.status === "completed" && request.completed_at ? (
                      <span
                        title={`Requested: ${formatDate(request.requested_at)}`}
                      >
                        {formatDate(request.completed_at)}
                      </span>
                    ) : (
                      formatDate(request.requested_at)
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {request.expires_at
                      ? formatDate(request.expires_at)
                      : "Never"}
                  </td>
                </tr>
              ))}

              {(deploymentRequests?.data ?? []).length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-6 py-8 text-center text-sm text-gray-500"
                  >
                    No deployment requests found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Detail Modal */}
      {selectedRequest && (
        <DeploymentRequestDetailModal
          request={selectedRequest}
          onClose={() => setSelectedRequest(null)}
          getStatusBadge={getStatusBadge}
          onViewDeployment={(deploymentId) => {
            setSelectedRequest(null);
            setSelectedDeploymentId(deploymentId);
          }}
        />
      )}

      {/* Deployment Details Modal */}
      {selectedDeploymentId && (
        <DeploymentDetailsModal
          deploymentId={selectedDeploymentId}
          onClose={() => setSelectedDeploymentId(null)}
        />
      )}

      {/* Pagination info */}
      {deploymentRequests && deploymentRequests.pagination.total > 0 && (
        <div className="mt-4 text-sm text-gray-500">
          Showing {(deploymentRequests.data ?? []).length} of{" "}
          {deploymentRequests.pagination.total} deployment requests
        </div>
      )}
    </div>
  );
}

// Detail modal with custom fields support
function DeploymentRequestDetailModal({
  request,
  onClose,
  getStatusBadge,
  onViewDeployment,
}: {
  request: DeploymentRequest;
  onClose: () => void;
  getStatusBadge: (status: string) => React.ReactNode;
  onViewDeployment?: (deploymentId: string) => void;
}) {
  // Parse custom_fields if it's a string
  const customFields =
    typeof request.custom_fields === "string"
      ? JSON.parse(request.custom_fields || "{}")
      : request.custom_fields || {};

  const customFieldEntries = Object.entries(customFields);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto scrollbar-hide"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900">
              Deployment Request Details
            </h2>
            {getStatusBadge(request.status)}
          </div>

          <dl className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <dt className="text-gray-500">Service</dt>
              <dd className="font-medium">{titleCase(request.service_name)}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Environment</dt>
              <dd className="font-medium">{request.environment_name}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Version</dt>
              <dd className="font-medium">{request.version}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Requested</dt>
              <dd className="font-medium">
                {formatDate(request.requested_at)}
              </dd>
            </div>
            {request.status === "completed" && request.completed_at && (
              <div>
                <dt className="text-gray-500">Completed</dt>
                <dd className="font-medium">
                  {formatDate(request.completed_at)}
                </dd>
              </div>
            )}
            {request.notes && (
              <div className="col-span-2">
                <dt className="text-gray-500">Notes</dt>
                <dd className="font-medium">{request.notes}</dd>
              </div>
            )}
          </dl>

          {/* Deployment Link - shows when completed and has deployment_id */}
          {request.status === "completed" &&
            request.deployment_id &&
            onViewDeployment && (
              <div className="mt-6 border-t pt-4">
                <button
                  onClick={() => onViewDeployment(request.deployment_id!)}
                  className="w-full flex items-center justify-between p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors group"
                >
                  <div className="flex items-center gap-3">
                    <Rocket className="h-5 w-5 text-green-600" />
                    <div className="text-left">
                      <div className="text-sm font-medium text-green-800">
                        Deployment Completed
                      </div>
                      <div className="text-xs text-green-600">
                        Click to view deployment details
                      </div>
                    </div>
                  </div>
                  <ArrowRight className="h-5 w-5 text-green-600 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            )}

          {/* Custom Fields */}
          {customFieldEntries.length > 0 && (
            <div className="mt-6 border-t pt-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3">
                Request Details
              </h3>
              <dl className="grid grid-cols-2 gap-4 text-sm">
                {customFieldEntries.map(([key, value]) => (
                  <div key={key}>
                    <dt className="text-gray-500 capitalize">
                      {key.replace(/-/g, " ")}
                    </dt>
                    <dd className="font-medium">
                      {isUrl(value as string) ? (
                        <a
                          href={value as string}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          {getUrlDisplayText(value as string, key)}
                        </a>
                      ) : (
                        (value as string) || "-"
                      )}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          <div className="mt-6 border-t pt-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3">
              Approvers
            </h3>
            {request.required_approvals &&
            request.required_approvals.length > 0 ? (
              <div className="space-y-2">
                {request.required_approvals.map((approver, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div>
                      <span className="font-medium capitalize">
                        {approver.role}
                      </span>
                      <span className="text-gray-500 ml-2">
                        {approver.email}
                      </span>
                    </div>
                    {getStatusBadge(approver.status)}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">
                {request.environment_name} doesn't require approval
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper to detect URLs
function isUrl(str: string): boolean {
  if (!str) return false;
  try {
    new URL(str);
    return str.startsWith("http://") || str.startsWith("https://");
  } catch {
    return false;
  }
}

// Helper to extract friendly display text from URLs
function getUrlDisplayText(url: string, fieldKey: string): string {
  if (!url) return url;

  try {
    const urlObj = new URL(url);

    // Jira ticket: extract ticket number (e.g., ED-25236 from /browse/ED-25236)
    if (
      fieldKey.toLowerCase().includes("jira") ||
      urlObj.pathname.includes("/browse/")
    ) {
      const match = urlObj.pathname.match(/\/browse\/([A-Z]+-\d+)/i);
      if (match) {
        return match[1];
      }
    }

    // Confluence/Wiki: extract page title from the last path segment
    if (
      fieldKey.toLowerCase().includes("release") ||
      fieldKey.toLowerCase().includes("notes") ||
      urlObj.pathname.includes("/wiki/")
    ) {
      const pathParts = urlObj.pathname.split("/").filter(Boolean);
      if (pathParts.length > 0) {
        // Get the last part and decode it, replace + with spaces
        const lastPart = decodeURIComponent(
          pathParts[pathParts.length - 1],
        ).replace(/\+/g, " ");
        return lastPart;
      }
    }

    // Default: just show the hostname + truncated path
    const path =
      urlObj.pathname.length > 30
        ? urlObj.pathname.substring(0, 30) + "..."
        : urlObj.pathname;
    return urlObj.hostname + path;
  } catch {
    // If URL parsing fails, truncate the string
    return url.length > 40 ? url.substring(0, 40) + "..." : url;
  }
}
